import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"

// Language picker — first step toward multi-language PCPs. Lets the user
// choose the plan's language from the toolbar. The choice persists on
// plan.meta.language (via plan_storage#savePlan, same as the plan-mode
// toggles) and broadcasts `pcp:language-change` so the translation layer
// we add next can react. No translation happens yet — this is the control.

const STORAGE_KEY = "pcp_plan_data"

// Languages offered to start. `code` is stored on plan.meta.language; `native`
// shows in the picker; `en` is the English name (tooltip / aria). Easy to
// extend — add a row here.
const LANGUAGES = [
  { code: "en", native: "English", en: "English" },
  { code: "es", native: "Español", en: "Spanish" },
  { code: "ko", native: "한국어",   en: "Korean" }
]

export default class extends Controller {
  static targets = ["button", "label", "menu"]

  connect() {
    this._onDocClick = (e) => { if (!this.element.contains(e.target)) this._close() }
    this._onKey      = (e) => { if (e.key === "Escape") this._close() }
    this._renderMenu()
    this._reflect()
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("keydown", this._onKey)
  }

  toggle(event) {
    event.stopPropagation()
    this.menuTarget.hidden ? this._open() : this._close()
  }

  select(event) {
    const code = event.currentTarget.dataset.lang
    const lang = LANGUAGES.find(l => l.code === code) || LANGUAGES[0]
    // DATA-LOSS GUARD: re-load the freshest plan before mutating + saving, so we
    // don't write a connect-time-stale snapshot over edits made since.
    const plan = this._load()
    if (!plan.meta) plan.meta = {}
    plan.meta.language = lang.code
    savePlan(plan)
    this._current = lang.code
    this._reflect()
    this._close()
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "meta.language", value: lang.code, plan }
    }))
    document.dispatchEvent(new CustomEvent("pcp:language-change", {
      bubbles: true, detail: { code: lang.code, name: lang.en, native: lang.native }
    }))
    this._toast(`Language: ${lang.native}`)
  }

  _renderMenu() {
    this.menuTarget.innerHTML = LANGUAGES.map(l => `
      <button type="button" role="option" class="en-lang-item" data-lang="${l.code}"
              data-action="language-picker#select">
        <span class="en-lang-native">${l.native}</span>
        <span class="en-lang-en">${l.en}</span>
      </button>`).join("")
  }

  _reflect() {
    const code = this._current || this._planLang()
    this._current = code
    const lang = LANGUAGES.find(l => l.code === code) || LANGUAGES[0]
    if (this.hasLabelTarget) this.labelTarget.textContent = lang.native
    this.menuTarget.querySelectorAll(".en-lang-item").forEach(el => {
      el.classList.toggle("is-active", el.dataset.lang === code)
    })
  }

  _planLang() {
    const plan = this._load()
    return (plan && plan.meta && plan.meta.language) || "en"
  }

  _open() {
    this.menuTarget.hidden = false
    this.buttonTarget.setAttribute("aria-expanded", "true")
  }
  _close() {
    if (this.hasMenuTarget) this.menuTarget.hidden = true
    if (this.hasButtonTarget) this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  _load() {
    try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : {} }
    catch (_) { return {} }
  }

  _toast(msg) {
    let bar = document.getElementById("en-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "en-toast"
      bar.style.cssText = "position:fixed;top:12px;right:12px;padding:10px 14px;border-radius:8px;font:600 13px/1.2 ui-sans-serif,system-ui;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.18);background:#dcfce7;color:#166534;pointer-events:none;"
      document.body.appendChild(bar)
    }
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._t)
    this._t = setTimeout(() => { bar.style.opacity = "0" }, 2000)
  }
}
